import React from 'react';
import { Button, Group, Modal, Stack } from '@cowbell/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { schemaToControls } from './schemaToControls';
import { buildValidationSchema } from './buildValidationSchema';
import { seedFormValues, toCoverageProps } from './coverageValue';
import type { FormValues } from './coverageValue';
import { FactoryControl } from './FactoryControl';
import type { RaterRulesByField, SchemaNode } from './types';

// Sit above the app's MUI layer (MUI dialogs/backdrops use ~1300), matching
// ExcessQuoteCartModal — otherwise the Mantine modal renders behind and looks
// like it never opened.
const MODAL_Z_INDEX = 1400;

export interface CoverageValue {
  props: Record<string, unknown>;
}

interface EndorsementModalProps {
  readonly opened: boolean;
  readonly title: string;
  readonly schema: SchemaNode;
  readonly rules?: RaterRulesByField;
  readonly existingProps?: Record<string, unknown>;
  readonly onClose: () => void;
  readonly onSave: (value: CoverageValue) => void;
}

// A single generic modal hosting the Inputs Factory form. Seeds from the
// rater-returned coverage.value.props (edit round-trip), validates via the
// schema+rule overlay, and emits a matching { props } on save. The factory core
// stays pure; this component owns the form wiring (FPX-6083).
export function EndorsementModal({
  opened,
  title,
  schema,
  rules,
  existingProps,
  onClose,
  onSave,
}: EndorsementModalProps) {
  const controls = React.useMemo(
    () => schemaToControls(schema, rules ?? {}),
    [schema, rules]
  );
  const validationSchema = React.useMemo(
    () => buildValidationSchema(schema, rules ?? {}),
    [schema, rules]
  );

  // Widen the modal when a row has many columns (e.g. a multi-field schedule),
  // capped so it stays usable. Single-column / scalar forms stay at 'lg'.
  const modalSize = React.useMemo(() => {
    const maxColumns = controls.reduce(
      (max, control) =>
        control.kind === 'array'
          ? Math.max(max, control.itemFields.length)
          : max,
      1
    );
    return maxColumns <= 1 ? 'lg' : Math.min(820, 280 + maxColumns * 130);
  }, [controls]);

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: seedFormValues(
      controls,
      existingProps as Parameters<typeof seedFormValues>[1]
    ),
  });

  const submit = methods.handleSubmit((values) => {
    onSave(toCoverageProps(controls, values));
    onClose();
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size={modalSize}
      centered
      zIndex={MODAL_Z_INDEX}
      transitionProps={{ transition: 'pop', duration: 200 }}
      styles={{ inner: { insetInlineStart: 0 } }}
    >
      <FormProvider {...methods}>
        <form onSubmit={submit}>
          <Stack>
            {controls.map((control) => (
              <FactoryControl key={control.fieldKey} control={control} />
            ))}
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </Group>
          </Stack>
        </form>
      </FormProvider>
    </Modal>
  );
}
